import {
  toWSDateString,
  WSCard,
  WSDataItem,
  WSGrid,
  WSInfoBox,
  WSList,
  WSPillProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { PayrollRun, PayrollRunCycleType } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { PAYROLL_CURRENCY } from "../../constants";
import { useQueryPayrollRunBreakdown } from "../../hooks/useQueryPayrollRunBreakdown";
import { selectorPayrollRunBreakdownAmounts } from "../../selectors/selectorPayrollRunBreakdownAmounts";
import { selectorPayrollRunNumber } from "../../selectors/selectorPayrollRunNumber";
import { selectorPayrollRunStatusPill } from "../../selectors/selectorPayrollRunStatusPill";
import {
  formatPayPeriodLabel,
  formatPayPeriodRange,
  parsePayrollAmount
} from "../../utils";

const OFF_CYCLE_PILL: WSPillProps = {
  theme: "neutral",
  badge: true,
  text: "Off-cycle"
};

const PAY_PERIOD_TOOLTIP = "The date range this payroll covers.";
const PAY_STATEMENTS_TOOLTIP =
  "Number of pay statements generated for this payroll.";

type Props = {
  payrollRun: PayrollRun;
  goToDetails: () => void;
};

export const PayrollRunCard: React.FC<Props> = ({
  payrollRun,
  goToDetails
}) => {
  // `listPayrollRuns` returns no breakdown, so the rollups are read per card.
  const queryBreakdown = useQueryPayrollRunBreakdown(payrollRun.id ?? "");
  const amounts = selectorPayrollRunBreakdownAmounts(queryBreakdown.data);

  const liabilityBreakdown = [
    { label: "Gross amount", amount: amounts.grossAmount },
    { label: "Payer taxes", amount: amounts.payerTaxes },
    { label: "Benefits", amount: amounts.benefits }
  ];

  return (
    <WSCard
      header={{
        label: formatPayPeriodLabel(payrollRun.payPeriodStartDate),
        value:
          payrollRun.cycleType === PayrollRunCycleType.OFF_CYCLE
            ? { pill: OFF_CYCLE_PILL }
            : undefined
      }}
      onClick={() => {
        goToDetails();
      }}
    >
      <WSGrid gutter="L">
        <WSGrid.Item span={{ s: "12", l: "6" }}>
          <WSList gap="S">
            <WSInfoBox>
              <WSText.Paragraph color="gray600" mb="XS" weight="medium">
                Liability amount
              </WSText.Paragraph>
              <WSText.Heading1 color="gray500" weight="book">
                {formatMoney(
                  parsePayrollAmount(payrollRun.totalAmount),
                  PAYROLL_CURRENCY
                )}
              </WSText.Heading1>
            </WSInfoBox>

            <WSInfoBox>
              <WSList gap="S">
                {liabilityBreakdown.map(item => (
                  <WSDataItem
                    key={item.label}
                    inline
                    label={item.label}
                    value={formatMoney(item.amount, PAYROLL_CURRENCY)}
                  />
                ))}
              </WSList>
            </WSInfoBox>
          </WSList>
        </WSGrid.Item>

        <WSGrid.Item span={{ s: "12", l: "6" }}>
          <WSGrid gutter="XL">
            <WSGrid.Item span="6">
              <WSDataItem
                label="Payroll ID"
                value={selectorPayrollRunNumber(payrollRun)}
              />
            </WSGrid.Item>

            <WSGrid.Item span="6">
              <WSDataItem
                label={{ text: "Pay period", tooltip: PAY_PERIOD_TOOLTIP }}
                value={formatPayPeriodRange(
                  payrollRun.payPeriodStartDate,
                  payrollRun.payPeriodEndDate
                )}
              />
            </WSGrid.Item>

            {payrollRun.events?.finalizedAt && (
              <WSGrid.Item span="6">
                <WSDataItem
                  label="Finalization date"
                  value={toWSDateString(
                    payrollRun.events.finalizedAt,
                    "monthDayYear"
                  )}
                />
              </WSGrid.Item>
            )}

            <WSGrid.Item span="6">
              <WSDataItem
                hideValue
                label="Status"
                pill={selectorPayrollRunStatusPill(payrollRun.status)}
              />
            </WSGrid.Item>

            <WSGrid.Item span="6">
              <WSDataItem
                label={{
                  text: "No. of pay statements",
                  tooltip: PAY_STATEMENTS_TOOLTIP
                }}
                value={
                  queryBreakdown.isLoading ? "—" : `${amounts.paystubsCount}`
                }
              />
            </WSGrid.Item>
          </WSGrid>
        </WSGrid.Item>
      </WSGrid>
    </WSCard>
  );
};
